// Het bewijs achter één regel van de Arbeidsmarkt-tab.
//
// Een waarneming draagt bewust GEEN oordeel: geen impact-score, geen duiding
// (W16 §6c — een oordeel zonder rubriek is een verzonnen oordeel). Wat er wél
// op staat is waar het vandaan komt. Vandaar deze volgorde: de vaststelling,
// het letterlijke citaat, de feiten eronder, en de bron die zichzelf
// verantwoordt.
//
// VORM (owner-feedback 2026-08-24: "de slide-ins zien er rommelig uit"): één
// ritme, niet zes. Het paneel had een blockquote, een uitklapper, een rijenlijst
// en twee omkaderde blokken die elk hun eigen randstijl hadden. Nu is er precies
// één omkadering — het citaat, omdat dat letterlijk uit de bron komt — en
// verder alleen kopjes met haarlijnen, zoals SourceDetail en SkillDetail.

import { useState } from "react";

import type { ObservatieDetailResponse } from "../contracts";
import { useMethodeV3 } from "../lib/meta";
import { color, radius, size, weight } from "../tokens";
import { periodeLabel, waardeLabel } from "./ArbeidsmarktWidgets";
import { PanelHeader, ReasonBadge, SectionLabel } from "./ui";

const THEMA_LABEL: Record<string, string> = {
  werkgelegenheid: "Werkgelegenheid",
  vraag: "Vraag naar personeel",
  aanbod: "Aanbod van personeel",
  beleid: "Beleid",
  lonen: "Lonen",
  technologie_in_werk: "Technologie in het werk",
  onduidelijk: "Thema onduidelijk",
};

const ONDERBOUWING_LABEL: Record<string, string> = {
  eigen_onderzoek: "Eigen onderzoek van de bron",
  cijfers_derden: "Cijfers van derden",
  bewering: "Bewering zonder onderbouwing",
  casus_anekdote: "Casus of anekdote",
  onduidelijk: "Onderbouwing onduidelijk",
};

const RICHTING_LABEL: Record<string, string> = {
  stijgend: "Stijgend",
  dalend: "Dalend",
  stabiel: "Stabiel",
  onduidelijk: "Richting onbenoemd",
};

const SCHAAL_LABEL: Record<string, string> = {
  lokaal: "Lokaal",
  regionaal: "Regionaal",
  nationaal: "Nationaal",
  EU: "Europees",
  mondiaal: "Mondiaal",
  onduidelijk: "Onduidelijk",
};

/** Eén feitenregel. Dezelfde maatvoering als de andere detailpanelen. */
function Regel({ label, waarde }: { label: string; waarde: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        padding: "8px 0",
        borderTop: `1px solid ${color.lineFaint}`,
        fontSize: size.body,
      }}
    >
      <div style={{ minWidth: 124, color: color.faint, flexShrink: 0 }}>{label}</div>
      <div style={{ color: waarde ? color.body : color.faint, flex: 1 }}>
        {waarde || "niet vermeld"}
      </div>
    </div>
  );
}

export function ObservatieDetail({
  detail,
  onClose,
  onReeks,
}: {
  detail: ObservatieDetailResponse;
  onClose: () => void;
  onReeks?: (bron: string, indicator: string) => void;
}) {
  const [context, setContext] = useState(false);
  // Methode v3: het gezagscijfer vervalt in de bronregel.
  const methodeV3 = useMethodeV3();
  const o = detail.observatie;
  const bron = o.bron;
  // Dezelfde haarlijn en dezelfde blokafstand als TrendDetail (owner-opname
  // a8b4k4swxqb1, 0:13: "stijl moet gelijk zijn aan de rest — whitespace,
  // clean"). De secties stonden tot nu tegen elkaar aan.
  const divider = (
    <div style={{ height: 1, background: color.lineFaint, marginBottom: 20 }} />
  );

  return (
    // EIGEN SCROLLGEBIED. Zonder deze wikkel loopt de inhoud onder de onderrand
    // van het paneel door en kom je er niet bij (owner-opname a8b4k4swxqb1,
    // 0:21). TrendDetail heeft hem wél; dit paneel niet, en dat is het hele
    // verschil. `height: 100%` want SlidePanel is fixed van top tot bottom.
    <div style={{ height: "100%", overflowY: "auto" }}>
      <PanelHeader onClose={onClose}>
        <ReasonBadge
          text={THEMA_LABEL[o.thema] ?? o.thema}
          title="Waar deze waarneming over gaat (leesveld thema)."
        />
        <ReasonBadge
          text={RICHTING_LABEL[o.richting] ?? o.richting}
          title="Welke kant het op beweegt volgens de bron."
        />
        {o.gebied && (
          <ReasonBadge text={o.gebied} title="De gebiedsnaam zoals de bron hem schrijft." />
        )}
      </PanelHeader>

      <div style={{ padding: 24 }}>
        <h2
          style={{
            fontSize: size.panelTitle,
            fontWeight: weight.bold,
            color: color.ink,
            lineHeight: 1.3,
            margin: 0,
          }}
        >
          {o.naam}
        </h2>
        {o.cijfer && (
          <p
            style={{
              fontSize: size.lead,
              color: color.body,
              fontWeight: weight.medium,
              margin: "8px 0 0",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {o.cijfer}
          </p>
        )}
        {o.samenvatting && (
          <p style={{ fontSize: size.body, color: color.muted, lineHeight: 1.6, marginTop: 10 }}>
            {o.samenvatting}
          </p>
        )}

        {divider}

        {/* De enige omkadering in dit paneel: dit is letterlijke brontekst. */}
        <div style={{ marginBottom: 20 }}>
        <SectionLabel>Wat de bron letterlijk zegt</SectionLabel>
        <blockquote
          style={{
            margin: "8px 0 0",
            padding: "12px 14px",
            background: color.surfaceAlt,
            borderRadius: radius.quote,
            fontSize: size.body,
            color: color.body,
            lineHeight: 1.6,
          }}
        >
          {detail.citaat || "Geen citaat vastgelegd."}
          {context && detail.citaatContext && (
            <span style={{ display: "block", marginTop: 10, color: color.soft }}>
              {detail.citaatContext}
            </span>
          )}
        </blockquote>
        {detail.citaatContext && (
          <button
            type="button"
            onClick={() => setContext((c) => !c)}
            style={{
              marginTop: 6,
              padding: 0,
              border: "none",
              background: "none",
              font: "inherit",
              fontSize: size.meta,
              color: color.soft,
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {context ? "Verberg de omliggende tekst" : "Toon de omliggende tekst"}
          </button>
        )}
        </div>

        {divider}

        <div style={{ marginBottom: 20 }}>
        <SectionLabel>De waarneming</SectionLabel>
        <div style={{ marginTop: 8 }}>
          <Regel label="Periode" waarde={o.peildatum} />
          <Regel
            label="Onderbouwing"
            waarde={o.onderbouwing ? ONDERBOUWING_LABEL[o.onderbouwing] ?? o.onderbouwing : null}
          />
          <Regel label="Sector" waarde={o.sector} />
          <Regel label="Beroepsgroep" waarde={o.beroepsgroep} />
          <Regel label="Schaal" waarde={SCHAAL_LABEL[o.schaal] ?? o.schaal} />
        </div>
        </div>

        {divider}

        <div style={{ marginBottom: 20 }}>
        <SectionLabel>Bron</SectionLabel>
        <div style={{ marginTop: 8, borderTop: `1px solid ${color.lineFaint}`, paddingTop: 10 }}>
          <div style={{ fontSize: size.body, color: color.ink, fontWeight: weight.medium }}>
            {bron.titel || <span style={{ color: color.soft }}>Zonder titel</span>}
          </div>
          <div style={{ fontSize: size.meta, color: color.soft, marginTop: 3 }}>
            {bron.uitgever}
            {bron.datum ? ` · ${bron.datum}` : " · datum onbekend"}
            {!methodeV3 && bron.gezag !== null && bron.gezag !== undefined ? ` · gezag ${bron.gezag}` : ""}
          </div>
          {bron.url && (
            <a
              href={bron.url}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: size.meta, color: color.ink, marginTop: 6, display: "inline-block" }}
            >
              Open de bron ↗
            </a>
          )}
        </div>
        </div>

        {detail.reeks && (
          <>
            {divider}
            <div style={{ marginBottom: 20 }}>
            <SectionLabel>Wat de cijfers hierover zeggen</SectionLabel>
            <button
              type="button"
              onClick={() => onReeks?.(detail.reeks!.bron, detail.reeks!.indicator)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 12,
                width: "100%",
                textAlign: "left",
                border: "none",
                borderTop: `1px solid ${color.lineFaint}`,
                padding: "10px 0 0",
                marginTop: 8,
                background: "none",
                cursor: onReeks ? "pointer" : "default",
                font: "inherit",
              }}
            >
              <span style={{ fontSize: size.body, color: color.body }}>
                {detail.reeks.titel}
                <span style={{ fontSize: size.meta, color: color.faint }}>
                  {" "}
                  — {detail.reeks.regio.naam}
                  {detail.reeks.laatste
                    ? `, ${periodeLabel(detail.reeks.laatste.periode, detail.reeks.periodeSoort)}`
                    : ""}
                </span>
              </span>
              <span
                style={{
                  fontSize: size.lead,
                  fontWeight: weight.medium,
                  color: color.ink,
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                }}
              >
                {detail.reeks.laatste
                  ? waardeLabel(detail.reeks.laatste.waarde, detail.reeks.eenheid)
                  : "—"}
              </span>
            </button>
            {detail.reeks.voorbehoud && (
              <p style={{ fontSize: size.meta, color: color.faint, marginTop: 6 }}>
                {detail.reeks.voorbehoud}
              </p>
            )}
            </div>
          </>
        )}

        {(detail.vlaggen ?? []).length > 0 && (
          <p style={{ fontSize: size.meta, color: color.faint, marginTop: 20, lineHeight: 1.6 }}>
            Gesignaleerd bij het lezen: {(detail.vlaggen ?? []).join(" · ")}. Deze vlaggen staan er
            bewust — stilte is de fout, niet de lege waarde.
          </p>
        )}
      </div>
    </div>
  );
}
